// Cash preferred — the card-fee box (2026-10-02).
//
// Reckless Heart Tattoo (Greenville SC), from its front desk: "If we could add a section for the customer to
// check a box acknowledging that there is a 2% fee if they pay with credit/debit card, and to avoid that fee they
// can pay with cash." Chris: "i'm imagining a setting that allows "cash preferred" to be specified as a setting
// with custom language, defaulting to off, and perhaps having some "default copy" that can be what Reckless Heart
// ends up with?"
//
// `public_data.cash_preferred` is OFF (absent, false, anything but a map) or the map the API's settings door
// wrote whole: the shop's `percent`, the owner's own `text` ("" for the default copy), the `id` (the fingerprint
// of both, the own clauses' rule: a changed figure or reworded box is a new id), and `sentence_en` /
// `sentence_es`, THE WORDS THE CLIENT READS, written on the row by the API so this form carries no copy of its
// own and the signed waiver prints the sentence this row carried (services/waiver-api, app.py). The form draws
// one box the client ticks before signing — after the shop's own clauses, before the terms — and posts
// `cashPreferred: {<id>: true}`. The owner's own words are shown as written in either language, the own
// clauses' rule. This module is the only place that decides what the key means (the ownClauses.ts shape):
// the rest of the form asks it.

import type { Lang } from "./i18n";

export type CashPreferred = { id: string; percent: string; sentence_en: string; sentence_es: string };

const ID = /^cp_[0-9a-f]{10}$/;

const clean = (value: unknown): string => `${value ?? ""}`.split(/\s+/).join(" ").trim();

/** The setting a shop's config carries, read tolerantly: anything short of an id and both sentences is OFF,
 *  never fatal, so a hand-written row can never blank a form. */
export function cashPreferredOf(value: unknown): CashPreferred | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const id = clean(raw.id);
  const en = clean(raw.sentence_en);
  const es = clean(raw.sentence_es);
  if (!ID.test(id) || !en) return null;
  return { id, percent: clean(raw.percent), sentence_en: en, sentence_es: es || en };
}

/** The words the client reads, in the language they picked. */
export function cashPreferredSentence(setting: CashPreferred, lang: Lang = "en"): string {
  return lang === "es" ? setting.sentence_es : setting.sentence_en;
}
